package com.vizio.pay.api

import com.vizio.redwolf.auth.api.AccountApi
import com.vizio.redwolf.auth.model.AccountAbuseFormInfo
import com.vizio.redwolf.auth.model.AccountEmailExistsRequestBody
import com.vizio.redwolf.auth.model.AccountForgotPasswordInfo
import com.vizio.redwolf.auth.model.AccountRegisterProductInfo
import com.vizio.redwolf.auth.model.AccountResult
import com.vizio.redwolf.auth.model.AccountSerialNumberInfo
import com.vizio.redwolf.auth.model.AccountShippingAddress
import com.vizio.redwolf.auth.model.AccountUnsubscribeInfo
import com.vizio.redwolf.auth.model.AccountUserEmailPreference
import com.vizio.redwolf.auth.model.SwapOneOrAllDevicesRequestInfo
import com.vizio.redwolf.auth.model.ConvertToWalmartAccountWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.CreateAccount2RequestInfo
import com.vizio.redwolf.auth.model.EmailVerifiedRequestInfo
import com.vizio.redwolf.auth.model.LoginRequestInfo
import com.vizio.redwolf.auth.model.MigrateGuestRequestInfo
import com.vizio.redwolf.auth.model.RequiresVerificationRequestInfo
import com.vizio.redwolf.auth.model.SendTermsRequestInfo
import com.vizio.redwolf.auth.model.SendVerificationEmailRequestInfo
import com.vizio.redwolf.auth.model.SocialLoginRequestInfo
import com.vizio.redwolf.auth.model.UpdateEmailRequestInfo
import com.vizio.redwolf.auth.model.UpdateEmailV2RequestInfo
import com.vizio.redwolf.auth.model.UpdatePasswordInfo
import com.vizio.redwolf.auth.model.UpdateUserDetailsRequestInfo
import com.vizio.redwolf.auth.model.UserDetailsRequestInfo
import com.vizio.redwolf.auth.model.ConvertWalmartAccountRequestInfo
import com.vizio.redwolf.auth.model.ConvertWalmartAccountWithTokenRequestInfo
import com.vizio.redwolf.auth.model.CreateWalmartAccountRequestInfo
import com.vizio.redwolf.auth.model.DeleteVizioAccountRequestInfo
import com.vizio.redwolf.auth.model.WalmartLoginRequestInfo
import com.vizio.redwolf.auth.model.EmailOTPRequestInfo
import com.vizio.redwolf.auth.model.EmailOTPWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.VerifyEmailOTPRequestInfo
import com.vizio.redwolf.auth.model.VerifyEmailOTPWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.WalmartAccountDeviceRelinkRequestInfo
import com.vizio.redwolf.auth.model.WalmartLoginViaSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.WebEmailVerificationRequestInfo
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import kotlin.js.Promise

@JsExport
class AccountJsApi(payEnv: PayEnvironment, jsClientConfig: JsClientConfig? = null) {
    private val accountApi = AccountApi(environment = payEnv,
                                        appConfig = AppConfig(""),
                                        clientConfig = jsClientConfig.toClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)
    )

    @JsName("createAccount")
    fun createAccount(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = CreateAccount2RequestInfo(
                email = jsRequestInfo.email as String,
                password = jsRequestInfo.password as String,
                firstName = jsRequestInfo.firstName as String?,
                lastName = jsRequestInfo.lastName as String?,
                postalCode = jsRequestInfo.postalCode as String?,
                phoneNumber = jsRequestInfo.phoneNumber as String?,
                reCaptchaToken = jsRequestInfo.reCaptchaToken as String?,
                birthDate = jsRequestInfo.birthDate as String?,
                gender = jsRequestInfo.gender as String?
            )
            accountApi.createAccount2(requestInfo, traceId = traceId)
        }
    }

    @JsName("login")
    fun login(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = LoginRequestInfo(
                email = jsRequestInfo.email as String,
                password = jsRequestInfo.password as String,
                reCaptchaToken = jsRequestInfo.reCaptchaToken as String?
            )
            accountApi.login(requestInfo, traceId = traceId)
        }
    }

    @JsName("socialLogin")
    fun socialLogin(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = SocialLoginRequestInfo(
                email = jsRequestInfo.email as String?,
                facebookToken = jsRequestInfo.facebookToken as String?,
                googleToken = jsRequestInfo.googleToken as String?,
                appleToken = jsRequestInfo.appleToken as String?,
                reCaptchaToken = jsRequestInfo.reCaptchaToken as String?
            )
            accountApi.socialLogin(requestInfo, traceId = traceId)
        }
    }

    @JsName("requestUserDetails")
    fun requestUserDetails(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = UserDetailsRequestInfo(
                accessToken = jsRequestInfo.accessToken as String
            )
            accountApi.requestUserDetails(requestInfo, traceId = traceId)
        }
    }

    @JsName("updateUserDetails")
    fun updateUserDetails(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = UpdateUserDetailsRequestInfo(
                accessToken = jsRequestInfo.accessToken as String,
                firstName = jsRequestInfo.firstName as String?,
                lastName = jsRequestInfo.lastName as String?,
                phoneNumber = jsRequestInfo.phoneNumber as String?,
                postalCode = jsRequestInfo.postalCode as String?,
                birthDate = jsRequestInfo.birthDate as String?,
                gender = jsRequestInfo.gender as String?
            )
            accountApi.updateUserDetails(requestInfo, traceId = traceId)
        }
    }

    @JsName("checkIfEmailVerified")
    fun checkIfEmailVerified(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = EmailVerifiedRequestInfo(
                accessToken = jsRequestInfo.accessToken as String
            )
            accountApi.checkIfEmailVerified(requestInfo, traceId = traceId)
        }
    }

    @JsName("checkIfRequiresEmailVerification")
    fun checkIfRequiresEmailVerification(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = RequiresVerificationRequestInfo(
                accessToken = jsRequestInfo.accessToken as String
            )
            accountApi.checkIfRequiresEmailVerification(requestInfo, traceId = traceId)
        }
    }

    @JsName("sendVerificationEmail")
    fun sendVerificationEmail(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = SendVerificationEmailRequestInfo(
                accessToken = jsRequestInfo.accessToken as String
            )
            accountApi.sendVerificationEmail(requestInfo, traceId = traceId)
        }
    }

    @JsName("sendTermsEmail")
    fun sendTermsEmail(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = SendTermsRequestInfo(
                accessToken = jsRequestInfo.accessToken as String
            )
            accountApi.sendTermsEmail(requestInfo, traceId = traceId)
        }
    }

    @JsName("migrateGuestAccount")
    fun migrateGuestAccount(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = MigrateGuestRequestInfo(
                accessToken = jsRequestInfo.accessToken as String,
                email = jsRequestInfo.email as String,
                password = jsRequestInfo.password as String
            )
            accountApi.migrateGuestAccount(requestInfo, traceId = traceId)
        }
    }

    @JsName("migrateGuestAccountV2")
    fun migrateGuestAccountV2(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = MigrateGuestRequestInfo(
                accessToken = jsRequestInfo.accessToken as String,
                email = jsRequestInfo.email as String,
                password = jsRequestInfo.password as String,
                birthDate = jsRequestInfo.birthDate as String?,
                gender = jsRequestInfo.gender as String?
            )
            accountApi.migrateGuestAccountV2(requestInfo, traceId = traceId)
        }
    }

    @JsName("verifyEmailToken")
    fun verifyEmailToken(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = WebEmailVerificationRequestInfo(
                accessToken = jsRequestInfo.accessToken as String,
                emailVerificationToken = jsRequestInfo.emailVerificationToken as String
            )
            accountApi.webVerifyToken(requestInfo, traceId = traceId)
        }
    }

    @JsName("updateEmail")
    fun updateEmail(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = UpdateEmailRequestInfo(
                accessToken = jsRequestInfo.accessToken as String,
                email = jsRequestInfo.email as String
            )
            accountApi.updateEmail(requestInfo, traceId = traceId)
        }
    }

    @JsName("updateEmailV2")
    fun updateEmailV2(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = UpdateEmailV2RequestInfo(
                accessToken = jsRequestInfo.accessToken as String,
                email = jsRequestInfo.email as String,
                reCaptchaToken = jsRequestInfo.reCaptchaToken as String?
            )
            accountApi.updateEmailV2(requestInfo, traceId = traceId)
        }
    }

    @JsName("resetPasswordRequest")
    fun resetPasswordRequest(jsResetPwInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val resetPwInfo = AccountForgotPasswordInfo(
                email = jsResetPwInfo.email as String,
                reCaptchaToken = jsResetPwInfo.reCaptchaToken as String?
            )
            accountApi.resetPasswordRequest(resetPwInfo, traceId = traceId)
        }
    }

    @JsName("updatePassword")
    fun updatePassword(token: String, jsPwInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val pwInfo = UpdatePasswordInfo(
                oldPassword = jsPwInfo.oldPassword as String,
                newPassword = jsPwInfo.newPassword as String
            )
            accountApi.updatePassword(token, pwInfo, traceId = traceId)
        }
    }

    @JsName("logout")
    fun logout(accessToken: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            accountApi.logout(authToken = accessToken, traceId = traceId)
        }
    }

    @JsName("getShippingAddress")
    fun getShippingAddress(authToken: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            accountApi.getShippingAddress(authToken, traceId = traceId)
        }
    }

    @JsName("updateShippingAddress")
    fun updateShippingAddress(authToken: String, jsNewAddress: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val newAddress = AccountShippingAddress(
                firstName = jsNewAddress.firstName as String,
                lastName = jsNewAddress.lastName as String,
                streetAddress = jsNewAddress.streetAddress as String?,
                additionalAddress = jsNewAddress.additionalAddress as String?,
                city = jsNewAddress.city as String?,
                state = jsNewAddress.state as String?,
                postalCode = jsNewAddress.postalCode as String?,
                phone = jsNewAddress.phone as String?
            )
            accountApi.updateShippingAddress(authToken, newAddress, traceId = traceId)
        }
    }

    @JsName("getRegisteredProducts")
    fun getRegisteredProducts(authToken: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            accountApi.getRegisteredProducts(authToken, traceId = traceId)
        }
    }

    @JsName("unregisterProduct")
    fun unregisterProduct(authToken: String, serialNumber: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            accountApi.unregisterProduct(authToken, serialNumber, traceId = traceId)
        }
    }

    @JsName("updateRegisteredProducts")
    fun updateRegisteredProducts(authToken: String, jsSerialNumbers: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val serialNumbers = (jsSerialNumbers as Array<String>).asList()
            accountApi.updateRegisteredProducts(authToken, serialNumbers, traceId = traceId)
        }
    }

    @JsName("registerProduct")
    fun registerProduct(authToken: String, jsRegisterProductInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val registerProductInfo = AccountRegisterProductInfo(
                firstName = jsRegisterProductInfo.firstName as String,
                lastName = jsRegisterProductInfo.lastName as String,
                streetAddress = jsRegisterProductInfo.streetAddress as String,
                additionalAddress = jsRegisterProductInfo.additionalAddress as String,
                city = jsRegisterProductInfo.city as String,
                state = jsRegisterProductInfo.state as String,
                postalCode = jsRegisterProductInfo.postalCode as String,
                phone = jsRegisterProductInfo.phone as String,
                email = jsRegisterProductInfo.email as String,
                emailOptIn = jsRegisterProductInfo.emailOptin as Boolean,
                serialNumbers = (jsRegisterProductInfo.serialNumbers as Array<String>).asList()
            )
            accountApi.registerProduct(authToken, registerProductInfo, traceId = traceId)
        }
    }


    @JsName("getEmailPreference")
    fun getEmailPreference(accessToken: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            accountApi.getEmailPreference(authToken = accessToken, traceId = traceId)
        }
    }

    @JsName("updateEmailPreference")
    fun updateEmailPreference(authToken: String, emailOptinput: Boolean, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val userPref = AccountUserEmailPreference(emailOptin = emailOptinput)
            accountApi.updateEmailPreference(authToken, userPref, traceId = traceId)
        }
    }

    @JsName("unsubscribe")
    fun unsubscribe(jsUnsubscribeInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val unsubscribeInfo = AccountUnsubscribeInfo(
                reCaptchaToken = jsUnsubscribeInfo.reCaptchaToken as String,
                email = jsUnsubscribeInfo.email as String
            )
            accountApi.unsubscribe(unsubscribeInfo, traceId = traceId)
        }
    }

    @JsName("createAbuseReport")
    fun createAbuseReport(authToken: String, abuseFormInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val abuseForm = AccountAbuseFormInfo(
                reportingReason = abuseFormInfo.reportingReason as String,
                userReported = abuseFormInfo.userReported as String,
                userReportedContactInfo = abuseFormInfo.userReportedContactInfo as String,
                issueDate = abuseFormInfo.issueDate as String,
                otherReason = abuseFormInfo.otherReason as String?,
                additionalDetails = abuseFormInfo.additionalDetails as String?
            )
            accountApi.createAbuseReport(authToken, abuseForm, traceId = traceId)
        }
    }

    @JsName("validateSerialNumber")
    fun validateSerialNumber(authToken: String, serialNumberInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val serialNumber = AccountSerialNumberInfo(
                serialNumber = serialNumberInfo.serialNumber as String
            )
            accountApi.validateSerialNumber(authToken, serialNumber, traceId = traceId)
        }
    }

    @JsName("emailExists")
    fun emailExists(jsEmailExistsInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val emailExistsInfo = AccountEmailExistsRequestBody(
                email = jsEmailExistsInfo.email as String,
                reCaptchaToken = jsEmailExistsInfo.reCaptchaToken as String?
            )
            accountApi.emailExists(emailExistsInfo, traceId = traceId)
        }
    }

    @JsName("walmartLogin")
    fun walmartLogin(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = WalmartLoginRequestInfo(
                authorizationCode = jsRequestInfo.authorizationCode as String,
                redirectUri = jsRequestInfo.redirectUri as String,
                reCaptchaToken = jsRequestInfo.reCaptchaToken as String?
            )
            accountApi.walmartLogin(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("walmartLoginWithSetupCode")
    fun walmartLoginWithSetupCode(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = WalmartLoginViaSetupCodeRequestInfo(
                authorizationCode = jsRequestInfo.authorizationCode as String,
                redirectUri = jsRequestInfo.redirectUri as String,
                setupCode = jsRequestInfo.setupCode,
                reCaptchaToken = jsRequestInfo.reCaptchaToken as String?
            )
            accountApi.walmartLoginWithSetupCode(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("createWalmartAccount")
    fun createWalmartAccount(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = CreateWalmartAccountRequestInfo(
                vzAuthCode = jsRequestInfo.vzAuthCode as String
            )
            accountApi.createWalmartAccount(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("convertToWalmartAccount")
    fun convertToWalmartAccount(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = ConvertWalmartAccountRequestInfo(
                vzAuthCode = jsRequestInfo.vzAuthCode as String
            )
            accountApi.convertToWalmartAccount(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("convertToWalmartAccountWithAccessToken")
    fun convertToWalmartAccountWithAccessToken(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = ConvertWalmartAccountWithTokenRequestInfo(
                vzAuthCode = jsRequestInfo.vzAuthCode as String,
                accessToken = jsRequestInfo.accessToken as String,
            )
            accountApi.convertToWalmartAccountWithAccessToken(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("convertToWalmartAccountWithSetupCode")
    fun convertToWalmartAccountWithSetupCode(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = ConvertToWalmartAccountWithSetupCodeRequestInfo(
                setupCode = jsRequestInfo.setupCode as String,
                vzAuthCode = jsRequestInfo.vzAuthCode as String
            )
            accountApi.convertToWalmartAccountWithSetupCode(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("deleteVizioAccount")
    fun deleteVizioAccount(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = DeleteVizioAccountRequestInfo(
                vzAuthCode = jsRequestInfo.vzAuthCode as String
            )
            accountApi.deleteVizioAccount(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("requestEmailOTP")
    fun requestEmailOTP(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = EmailOTPRequestInfo(
                vzAuthCode = jsRequestInfo.vzAuthCode as String
            )
            accountApi.requestEmailOTP(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("verifyEmailOTP")
    fun verifyEmailOTP(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = VerifyEmailOTPRequestInfo(
                vzAuthCode = jsRequestInfo.vzAuthCode as String,
                password = jsRequestInfo.password as String
            )
            accountApi.verifyEmailOTP(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("requestEmailOTPWithSetupCode")
    fun requestEmailOTPWithSetupCode(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = EmailOTPWithSetupCodeRequestInfo(
                setupCode = jsRequestInfo.setupCode as String
            )
            accountApi.requestEmailOTPWithSetupCode(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("verifyEmailOTPWithSetupCode")
    fun verifyEmailOTPWithSetupCode(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = VerifyEmailOTPWithSetupCodeRequestInfo(
                setupCode = jsRequestInfo.setupCode as String,
                password = jsRequestInfo.password as String
            )
            accountApi.verifyEmailOTPWithSetupCode(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("walmartAccountDeviceRelink")
    fun walmartAccountDeviceRelink(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = WalmartAccountDeviceRelinkRequestInfo(
                setupCode = jsRequestInfo.setupCode as String,
                accessToken = jsRequestInfo.accessToken as String,
            )
            accountApi.walmartAccountDeviceRelink(requestInfo = requestInfo, traceId = traceId)
        }
    }

    @JsName("swapOneOrAllDevices")
    fun swapOneOrAllDevices(jsRequestInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleAccountJsException) {
            val requestInfo = SwapOneOrAllDevicesRequestInfo(
                setupCode = jsRequestInfo.setupCode as String,
                allDevices = jsRequestInfo.allDevices as Boolean,
                accessToken = jsRequestInfo.accessToken as String,
            )
            accountApi.swapOneOrAllDevices(requestInfo = requestInfo, traceId = traceId)
        }
    }

    private fun <T: Any> handleAccountJsException(ex: Exception, data: JsExceptionInfo<T>? = null): AccountResult.Error {
        return AccountResult.Error(ResponseHeaders.Invalid, Exception(jsRequestExceptionError))
    }

}
